import React, { useState, useRef, DragEvent, ChangeEvent, useEffect } from 'react';
import { Download, Upload, CheckCircle, XCircle, AlertCircle, Users, FileSpreadsheet, Clock, Check } from 'lucide-react';
import api from '../services/api';
import LoadingState, { Spinner } from './common/LoadingState';
import { ImportProgressModal } from './ImportProgressModal';
import { useConfirm } from './confirm/ConfirmDialogProvider';
import { notifyError, notifySuccess, notifyWarning } from '../utils/notificationCenter';

interface ImportResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: ImportError[];
  users: ImportedUser[];
  multiApartmentUsers?: {
    email: string;
    apartmentCount: number;
    totalCoefficient: number;
  }[];
  emailsSent?: number;
  emailsFailed?: number;
  emailErrors?: string[];
}

interface ImportError {
  row: number;
  data: Record<string, any>;
  error: string;
}

interface ImportedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  apartment: string; // Puede contener múltiples apartamentos separados por coma
  coefficient: number; // Coeficiente total (suma si tiene múltiples apartamentos)
  temporaryPassword: string;
  apartmentCount?: number; // Número de apartamentos
  totalCoefficient?: number; // Coeficiente total calculado
}

interface ResidentialComplex {
  id: string;
  name: string;
  address: string;
}

export const UserImportComponent: React.FC = () => {
  const { confirm } = useConfirm();
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [sendEmails, setSendEmails] = useState(true); // Activado por defecto
  const [useDocumentAsCredentials, setUseDocumentAsCredentials] = useState(false);
  const [complexes, setComplexes] = useState<ResidentialComplex[]>([]);
  const [selectedComplex, setSelectedComplex] = useState<string>('');
  const [selectedComplexName, setSelectedComplexName] = useState<string>('');
  const [loadingComplexes, setLoadingComplexes] = useState(true);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [importHistory, setImportHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cargar historial al montar el componente
  useEffect(() => {
    loadImportHistory();
  }, []);

  // Cargar historial de importaciones
  const loadImportHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.api.get('/users/import-history');
      const payload = (response as any).data;
      const history = Array.isArray(payload?.data) ? payload.data : [];
      setImportHistory(history);
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Reabrir un job de importación anterior
  const reopenImportJob = (jobId: string) => {
    setImportJobId(null);
    setTimeout(() => {
      setImportJobId(jobId);
      setShowProgressModal(true);
    }, 0);
  };

  // Completar manualmente una importación stuck
  const completeImportJobManually = async (jobId: string) => {
    const shouldComplete = await confirm({
      title: 'Completar importación',
      message: '¿Marcar esta importación como completada? Se dejará de procesar.',
      confirmText: 'Completar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldComplete) {
      return;
    }

    try {
      await api.api.post(`/users/import/${jobId}/complete`);
      notifySuccess('Importación marcada como completada');
      loadImportHistory(); // Recargar historial
    } catch (error: any) {
      notifyError('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  // Cargar conjuntos al montar el componente
  useEffect(() => {
    const fetchComplexes = async () => {
      try {
        const response = await api.api.get<{ success: boolean; data: ResidentialComplex[] }>(
          '/complexes'
        );
        const payload = (response as any).data;
        const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        setComplexes(list);
        if (list.length > 0) {
          setSelectedComplex(list[0].id);
          setSelectedComplexName(list[0].name);
        }
      } catch (error) {
        console.error('Error cargando conjuntos:', error);
        notifyError('Error al cargar la lista de conjuntos residenciales');
      } finally {
        setLoadingComplexes(false);
      }
    };
    fetchComplexes();
  }, []);

  // Manejo de drag & drop
  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && isValidExcelFile(droppedFile)) {
      setFile(droppedFile);
      setResult(null);
    } else {
      notifyWarning('Por favor, sube un archivo Excel válido (.xlsx o .xls)');
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && isValidExcelFile(selectedFile)) {
      setFile(selectedFile);
      setResult(null);
    } else {
      notifyWarning('Por favor, selecciona un archivo Excel válido (.xlsx o .xls)');
    }
  };

  const isValidExcelFile = (file: File): boolean => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const validExtensions = ['.xlsx', '.xls'];
    return (
      validTypes.includes(file.type) ||
      validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    );
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!selectedComplex) {
      notifyWarning('Por favor selecciona un conjunto residencial');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('complexId', selectedComplex);
      formData.append('complexName', selectedComplexName);
      formData.append('sendEmails', String(sendEmails));
      formData.append('useDocumentAsCredentials', String(useDocumentAsCredentials));
      formData.append('file', file);

      const response = await api.api.post<{ success: boolean; importJobId: string; message: string }>(
        '/users/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const jobId = response.data.importJobId;
      setImportJobId(jobId);
      setShowProgressModal(true);
      setFile(null);
      setResult(null);
    } catch (error: any) {
      notifyError(error.response?.data?.message || 'Error al importar usuarios');
    } finally {
      setUploading(false);
    }
  };

  const handleComplexChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const complexId = e.target.value;
    setSelectedComplex(complexId);
    const complex = complexes.find(c => c.id === complexId);
    if (complex) {
      setSelectedComplexName(complex.name);
    }
  };

  const handleImportComplete = () => {
    // Limpiar estado
    setShowProgressModal(false);
    setImportJobId(null);
    // El usuario puede cerrar el modal y continuar
  };
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.api.get('/users/import/template', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_usuarios.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      notifyError('Error al descargar la plantilla');
    }
  };

  const handleDownloadResults = () => {
    if (!result) return;

    const csvContent = [
      'Email,Nombre,Apellido,Apartamento,Coeficiente,Contraseña Temporal',
      ...result.users.map(
        (u) =>
          `${u.email},${u.firstName},${u.lastName},${u.apartment},${u.coefficient},${u.temporaryPassword}`
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'usuarios_importados.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Importación Masiva de Usuarios
              </h1>
              <p className="text-gray-600 mt-1">
                Crea múltiples usuarios propietarios de forma rápida y sencilla
              </p>
            </div>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-5 h-5" />
            <span>Descargar Plantilla</span>
          </button>
        </div>
      </div>

      {/* Instrucciones */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h2 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          Instrucciones
        </h2>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li>Descarga la plantilla de Excel haciendo clic en el botón verde</li>
          <li>Completa la información de los usuarios en las columnas correspondientes</li>
          <li>
            Las columnas <strong>requeridas</strong> son: Torre, Apartamento, Nombre, Correo y
            Coeficiente
          </li>
          <li><strong>Selecciona el conjunto residencial</strong> antes de subir el archivo</li>
          <li>
            <strong className="text-blue-900">Múltiples apartamentos:</strong> Si una persona posee varios 
            apartamentos, repite su correo en diferentes filas (una por cada apartamento). 
            El sistema detectará automáticamente que es el mismo propietario y <strong>sumará todos 
            los coeficientes</strong> para reflejar el peso real de su voto.
          </li>
          <li>Guarda el archivo Excel y súbelo arrastrándolo o haciendo clic en el área de carga</li>
          <li>Revisa el resultado y descarga las contraseñas generadas</li>
        </ol>
      </div>

      {/* Selector de conjunto residencial */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <label htmlFor="complex-select" className="block text-sm font-medium text-gray-700 mb-2">
          Conjunto Residencial *
        </label>
        {loadingComplexes ? (
          <div className="text-gray-500">Cargando conjuntos...</div>
        ) : complexes.length === 0 ? (
          <div className="text-red-600">No hay conjuntos disponibles. Por favor, crea uno primero.</div>
        ) : (
          <select
            id="complex-select"
            value={selectedComplex}
            onChange={handleComplexChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {complexes.map((complex) => (
              <option key={complex.id} value={complex.id}>
                {complex.name} - {complex.address}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Zona de carga */}
      {!result && (
        <div
          className={`bg-white rounded-lg shadow-sm p-8 transition-all ${
            dragActive ? 'border-4 border-blue-500 border-dashed bg-blue-50' : 'border-2 border-gray-200 border-dashed'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <FileSpreadsheet className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            {file ? (
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{file.name}</span>
                </div>
                
                {/* Checkbox para enviar emails */}
                <div className="flex items-center justify-center space-x-2">
                  <input
                    type="checkbox"
                    id="sendEmails"
                    checked={sendEmails}
                    onChange={(e) => setSendEmails(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="sendEmails" className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <Download className="h-4 w-4 text-blue-600" aria-hidden="true" />
                    <span>Enviar emails de bienvenida con credenciales (solo usuarios nuevos)</span>
                  </label>
                </div>

                <div className="flex flex-col items-center space-y-1">
                  <div className="flex items-center justify-center space-x-2">
                    <input
                      type="checkbox"
                      id="useDocumentAsCredentials"
                      checked={useDocumentAsCredentials}
                      onChange={(e) => setUseDocumentAsCredentials(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="useDocumentAsCredentials" className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <Users className="h-4 w-4 text-blue-600" aria-hidden="true" />
                      <span>Usar cédula/documento como usuario y contraseña (solo usuarios nuevos)</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Los usuarios existentes no cambian contraseña aunque aparezcan en el Excel.
                  </p>
                </div>
                
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => setFile(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                  >
                    {uploading ? (
                      <>
                        <Spinner size="sm" className="border-white" />
                        <span>Importando...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        <span>Importar Usuarios</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-lg text-gray-700 mb-2">
                  Arrastra tu archivo Excel aquí o haz clic para seleccionar
                </p>
                <p className="text-sm text-gray-500 mb-4">Formatos aceptados: .xlsx, .xls</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileInput}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Upload className="w-5 h-5" />
                  <span>Seleccionar Archivo</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Resultados */}
      {result && (
        <div className="space-y-6">
          {/* Resumen */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Resultado de la Importación</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="text-blue-600 text-sm font-medium mb-1">Total de Filas</div>
                <div className="text-3xl font-bold text-blue-900">{result.totalRows}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-green-600 text-sm font-medium mb-1">Importados Exitosamente</div>
                <div className="text-3xl font-bold text-green-900">{result.successCount}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="text-red-600 text-sm font-medium mb-1">Errores</div>
                <div className="text-3xl font-bold text-red-900">{result.errorCount}</div>
              </div>
            </div>
            
            {/* Estadísticas de Emails */}
            {result.emailsSent !== undefined && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Download className="h-4 w-4 text-gray-600" aria-hidden="true" />
                  <span>Envío de Emails</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="text-green-700 text-sm font-medium">Emails Enviados</span>
                      <span className="text-2xl font-bold text-green-900">{result.emailsSent}</span>
                    </div>
                  </div>
                  {result.emailsFailed! > 0 && (
                    <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                      <div className="flex items-center justify-between">
                        <span className="text-orange-700 text-sm font-medium">Emails Fallidos</span>
                        <span className="text-2xl font-bold text-orange-900">{result.emailsFailed}</span>
                      </div>
                    </div>
                  )}
                </div>
                {result.emailErrors && result.emailErrors.length > 0 && (
                  <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-orange-800 mb-1">Errores de envío:</p>
                    <ul className="text-xs text-orange-700 space-y-1">
                      {result.emailErrors.slice(0, 3).map((error, i) => (
                        <li key={i}>• {error}</li>
                      ))}
                      {result.emailErrors.length > 3 && (
                        <li className="font-medium">... y {result.emailErrors.length - 3} más</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Alerta de usuarios con múltiples apartamentos */}
          {result.multiApartmentUsers && result.multiApartmentUsers.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-blue-900 mb-2">
                    Propietarios con Múltiples Apartamentos
                  </h3>
                  <p className="text-sm text-blue-700 mb-3">
                    Se detectaron {result.multiApartmentUsers.length} propietario(s) con múltiples apartamentos. 
                    Sus coeficientes se han sumado automáticamente para reflejar el peso real de su voto.
                  </p>
                  <div className="space-y-2">
                    {result.multiApartmentUsers.map((user, index) => (
                      <div key={index} className="bg-white rounded-md p-3 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{user.email}</span>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-blue-700">
                              <strong>{user.apartmentCount}</strong> apartamentos
                            </span>
                            <span className="text-green-700 font-semibold">
                              Coeficiente total: {user.totalCoefficient.toFixed(10)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Usuarios importados exitosamente */}
          {result.users.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  Usuarios Importados
                </h3>
                <button
                  onClick={handleDownloadResults}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar Credenciales</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Nombre</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Apartamento(s)</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Coeficiente Total</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Contraseña Temporal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {result.users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{user.email}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {user.firstName} {user.lastName}
                          {user.apartmentCount && user.apartmentCount > 1 && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {user.apartmentCount} aptos
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="max-w-xs" title={user.apartment}>
                            {user.apartment}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-semibold ${user.apartmentCount && user.apartmentCount > 1 ? 'text-green-700' : 'text-gray-700'}`}>
                            {user.totalCoefficient || user.coefficient}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm bg-gray-50 text-gray-900">
                          {user.temporaryPassword}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Errores */}
          {result.errors.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                Errores Encontrados
              </h3>
              <div className="space-y-3">
                {result.errors.map((error, index) => (
                  <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-900">
                          Fila {error.row}: {error.error}
                        </div>
                        <div className="text-xs text-red-700 mt-1">
                          Datos: {JSON.stringify(error.data)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botón para nueva importación */}
          <div className="text-center">
            <button
              onClick={() => {
                setResult(null);
                setFile(null);
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Realizar Nueva Importación
            </button>
          </div>
        </div>
      )}

      {/* Historial de Importaciones */}
      <div className="bg-white rounded-lg shadow-sm p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Clock className="w-6 h-6 text-gray-600 mr-2" />
          Historial de Importaciones
        </h2>

        {loadingHistory ? (
          <LoadingState message="Cargando historial..." />
        ) : importHistory.length === 0 ? (
          <div className="text-center py-8">
            <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600">No hay importaciones anteriores</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Archivo</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Estado</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Progreso</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Enviados</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Fallidos</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Fecha</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Acción</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map((job) => {
                  const isCompleted = job.status === 'completed' || job.status === 'failed';
                  const progress = job.totalUsers > 0 
                    ? Math.round((job.processedUsers / job.totalUsers) * 100)
                    : 0;
                  const startedAt = job.startedAt ? new Date(job.startedAt) : null;

                  return (
                    <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-gray-900 font-medium">{job.fileName}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            job.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : job.status === 'processing'
                              ? 'bg-blue-100 text-blue-800'
                              : job.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {job.status === 'completed' && <Check className="w-3 h-3 mr-1" />}
                          {job.status === 'processing' && <Spinner size="sm" className="mr-1" />}
                          {job.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="w-32">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-600">{progress}%</span>
                            <span className="text-xs text-gray-600">{job.processedUsers}/{job.totalUsers}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                job.status === 'completed' && job.emailsFailed === 0
                                  ? 'bg-green-600'
                                  : job.status === 'failed'
                                  ? 'bg-red-600'
                                  : 'bg-blue-600'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">{job.emailsSent}</td>
                      <td className="py-3 px-4 text-sm text-red-600 font-medium">{job.emailsFailed}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {startedAt?.toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => reopenImportJob(job.id)}
                            className="text-blue-600 hover:text-blue-900 transition-colors text-sm font-medium"
                          >
                            Ver detalles →
                          </button>
                          {job.status === 'processing' && (
                            <button
                              onClick={() => completeImportJobManually(job.id)}
                              className="text-xs px-2 py-1 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded transition-colors"
                              title="Completar importación stuck"
                            >
                              ✓ Completar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Progreso de Importación */}
      <ImportProgressModal
        importJobId={importJobId || ''}
        isOpen={showProgressModal}
        onClose={() => {
          setShowProgressModal(false);
          loadImportHistory(); // Recargar historial al cerrar
        }}
      />
    </div>
  );
};
